// Single source of truth for turning any image reference stored in the CMS
// (legacy `/images/...` static assets, `/api/media/...` object-storage URLs,
// or already-absolute `http(s)://...` URLs) into a browser-usable src.
//
// Why this helper exists:
//   `API` from `@/lib/api` is `${REACT_APP_BACKEND_URL}/api`, i.e. it ALREADY
//   ends in `/api`. Prepending it to a value that also starts with `/api/...`
//   (which every /api/media/... URL does) produces `.../api/api/media/...`
//   → 404. This helper strips that duplication and works for every legacy
//   reference we ship, so callers never have to know which flavour they hold.
import { API } from "@/lib/api";

// Trim `/api` from the end of the base so we can safely prepend paths that
// begin with `/api/...`. Everything else (relative, absolute, /images/...)
// keeps working.
const ORIGIN = (API || "").replace(/\/api\/?$/, "");

/**
 * Resolve any stored image reference into a real, fetchable URL.
 *
 * Accepts:
 *   - "" | null | undefined       → returns ""
 *   - "http(s)://…"               → returned unchanged
 *   - "/api/media/…"              → `${ORIGIN}/api/media/…`
 *   - "/images/…" (static build)  → `${ORIGIN}/images/…`
 *   - any other relative path     → `${ORIGIN}<path>`
 */
export function resolveImageUrl(ref) {
  if (!ref || typeof ref !== "string") return "";
  const v = ref.trim();
  if (!v) return "";
  if (v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:")) return v;
  if (v.startsWith("/")) return `${ORIGIN}${v}`;
  // Bare object-storage path (no leading slash) — assume /api/media/ prefix.
  return `${ORIGIN}/api/media/${v.replace(/^\/+/, "")}`;
}

export default resolveImageUrl;
